import type { ISecapProductDataSource } from '../../interfaces/secap/ISecapApi';
import type {
  ISecapSourceRowViewModel,
  ISecapSourcesViewModel,
  SecapSourceDisplayStatus,
} from '../../interfaces/secap/ISecapSources';

const SOURCE_TYPE_LABELS = {
  api: 'API',
  ogc: 'Servizio OGC',
  open_data: 'Open data',
  periodic_import: 'Import periodico',
  user_upload: 'Caricamento ente',
} as const;

const ROLE_LABELS = {
  plan_source: 'Fonte del piano',
  comparison: 'Confronto',
} as const;

const REFRESH_LABELS = {
  manual: 'Manuale',
  daily: 'Giornaliero',
  weekly: 'Settimanale',
  monthly: 'Mensile',
  quarterly: 'Trimestrale',
  yearly: 'Annuale',
} as const;

const INGESTION_STATUS_LABELS = {
  queued: 'In coda',
  running: 'In corso',
  completed: 'Completato',
  failed: 'Fallito',
} as const;

const STATUS_LABELS: Readonly<Record<SecapSourceDisplayStatus, string>> = {
  active: 'Attiva',
  updated: 'Aggiornata',
  queued: 'In coda',
  running: 'In aggiornamento',
  error: 'Errore',
  inactive: 'Disattivata',
};

const getSourceStatus = (source: ISecapProductDataSource): SecapSourceDisplayStatus => {
  if (!source.is_active) {
    return 'inactive';
  }
  if (source.last_attempt_status === 'failed' || source.last_error) {
    return 'error';
  }
  if (source.last_attempt_status === 'running') {
    return 'running';
  }
  if (source.last_attempt_status === 'queued') {
    return 'queued';
  }
  if (source.last_attempt_status === 'completed' || source.last_success_at) {
    return 'updated';
  }
  return 'active';
};

const toSourceRow = (source: ISecapProductDataSource): ISecapSourceRowViewModel => {
  const status = getSourceStatus(source);
  return {
    uuid: source.uuid,
    code: source.code,
    name: source.name,
    sourceType: source.source_type,
    sourceTypeLabel: SOURCE_TYPE_LABELS[source.source_type],
    role: source.current_config.role,
    roleLabel: ROLE_LABELS[source.current_config.role],
    status,
    statusLabel: STATUS_LABELS[status],
    isActive: source.is_active,
    lastAttemptAt: source.last_attempt_at,
    lastAttemptStatusLabel: source.last_attempt_status
      ? INGESTION_STATUS_LABELS[source.last_attempt_status]
      : '—',
    lastSuccessAt: source.last_success_at,
    nextRefreshAt: source.next_refresh_at,
    lastError: source.last_error,
    format: source.current_config.format,
    crs: source.current_config.crs,
    refreshFrequency: source.current_config.refresh_frequency,
    refreshFrequencyLabel: source.current_config.refresh_frequency
      ? REFRESH_LABELS[source.current_config.refresh_frequency]
      : 'Non pianificato',
    redistributable: source.current_config.redistributable,
    mappingCount: source.current_config.mappings.length,
  };
};

export const buildSecapSourcesViewModel = (
  dataSources: readonly ISecapProductDataSource[],
): ISecapSourcesViewModel => ({
  summary: {
    planSources: dataSources.filter((source) => source.current_config.role === 'plan_source').length,
    comparisonSources: dataSources.filter(
      (source) => source.current_config.role === 'comparison',
    ).length,
    userUploads: dataSources.filter((source) => source.source_type === 'user_upload').length,
    activeSources: dataSources.filter((source) => source.is_active).length,
    errorSources: dataSources.filter(
      (source) => source.last_attempt_status === 'failed' || Boolean(source.last_error),
    ).length,
    total: dataSources.length,
  },
  sources: dataSources.map(toSourceRow),
});
